import type { LinkProps } from '@chakra-ui/react'
import { Grid } from '@chakra-ui/react'
import type { MouseEvent, Ref } from 'react'
import { forwardRef } from 'react'

import { NavLink } from '../../../components/NavLink'
import { RoundBadge } from '../../../components/RoundBadge'
import type { PageSection } from './Sidebar'
import { useScrollToPageSection } from './Sidebar'

interface PageSectionItemProps extends LinkProps {
  isCurrent?: boolean
  isDisabled?: boolean
  badge?: number | string
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void
  subsections?: PageSection[]
}

export const PageSectionItem
  = forwardRef(
    (
      {
        children,
        isCurrent = false,
        isDisabled = false,
        badge,
        onClick,
        subsections,
        ...rest
      }: PageSectionItemProps,
      ref: Ref<HTMLAnchorElement> | undefined,
    ) => {
      const scrollToSection = useScrollToPageSection()

      const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault()

        const href = e.currentTarget.href.split('#')[1]
        const elementId = href.replace(/#/, '')

        scrollToSection(elementId)

        onClick?.(e)
      }

      return (
        <Grid>
          <NavLink
            ref={ref}
            onClick={handleClick}
            isCurrent={isCurrent}
            isDisabled={isDisabled}
            {...rest}
          >
            {children}

            {badge && <RoundBadge sx={{ my: '-10px' }}>{badge}</RoundBadge>}
          </NavLink>

          {subsections && (
            <Grid sx={{ ml: 4, gap: 2 }}>
              {subsections.map((subsection, index) => {
                const { isDisabled: isSubsectionDisabled } = subsection

                return (
                  <NavLink
                    key={index}
                    href={`#${subsection.id}`}
                    onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                      e.preventDefault()
                    }}
                    isDisabled={isSubsectionDisabled}
                    sx={{
                      py: 0,
                      fontWeight: 'medium',
                    }}
                  >
                    {subsection.title ?? `${subsection.index} ${subsection.sectionTitle}`}
                  </NavLink>
                )
              })}
            </Grid>
          )}
        </Grid>
      )
    },
  )
